import React, { useMemo } from 'react';
import {
  CheckBadgeIcon,
  ShieldCheckIcon,
  SparklesIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowRightIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

import type { Candidate, Citation, Criterion, Evaluation, UploadedFile } from '../types';
import type { CriterionColor } from '../utils';

interface Props {
  candidate: Candidate | null;
  criteria: Criterion[]; // critères feuilles actifs (scope mode "Tout")
  evaluations: Evaluation[]; // toutes les évaluations du projet
  citations: Citation[]; // citations du candidat déjà filtrées par le parent
  files: UploadedFile[];
  colorMap: Map<string, CriterionColor>;
  criteriaValidated: boolean;
  activeCitationId: string | null;
  hoveredCitationId: string | null;
  onSelectCitation: (id: string | null) => void;
  onHoverCitation: (id: string | null) => void;
  onVerifyCitation: (id: string, verified: boolean) => void;
  onRemoveCitation: (id: string) => void;
  /** Valide d'un coup une liste de citations IA (non vérifiées). */
  onBulkValidateAi: (citationIds: string[]) => void;
  /** Bascule vers la vue "un critère" sur le critère donné. */
  onFocusCriterion: (criterionId: string) => void;
}

/**
 * Panneau de droite du workspace en mode "Tout".
 *
 * Remplace `EvaluationEditor` quand `viewMode === 'all'`. Présente les
 * citations du candidat courant regroupées par critère, avec :
 *  - un bouton "Tout valider IA" global (toutes citations IA non vérifiées),
 *  - par groupe : pastille couleur critère + bouton de validation en masse
 *    limitée à ce critère + raccourci "Voir le critère" (retour au focus).
 *
 * Validation = passage de `verified: false` à `verified: true` sur les
 * citations d'origine IA uniquement. Les citations manuelles sont déjà
 * intentionnelles et ne sont pas touchées par la validation de masse.
 */
const AllCitationsPanel: React.FC<Props> = ({
  candidate,
  criteria,
  evaluations,
  citations,
  files,
  colorMap,
  criteriaValidated,
  activeCitationId,
  hoveredCitationId,
  onSelectCitation,
  onHoverCitation,
  onVerifyCitation,
  onRemoveCitation,
  onBulkValidateAi,
  onFocusCriterion,
}) => {
  // Index rapides
  const evalByCitation = useMemo(() => {
    const evalById = new Map(evaluations.map(e => [e.id, e]));
    const map = new Map<string, Evaluation>();
    citations.forEach(c => {
      const ev = evalById.get(c.evaluationId);
      if (ev) map.set(c.id, ev);
    });
    return map;
  }, [citations, evaluations]);

  // Regroupement par critère (trié par code croissant)
  const groups = useMemo(() => {
    const byCritId = new Map<string, Citation[]>();
    citations.forEach(c => {
      const ev = evalByCitation.get(c.id);
      if (!ev) return;
      const bucket = byCritId.get(ev.criterionId) ?? [];
      bucket.push(c);
      byCritId.set(ev.criterionId, bucket);
    });
    return criteria
      .map(crit => ({
        criterion: crit,
        citations: byCritId.get(crit.id) ?? [],
      }))
      // On garde les critères sans citations pour donner une vue d'ensemble
      // (l'analyste voit "il me manque des citations sur ce critère")
      .sort((a, b) =>
        a.criterion.code.localeCompare(b.criterion.code, 'fr', { numeric: true })
      );
  }, [citations, evalByCitation, criteria]);

  // Compteurs globaux
  const aiPending = useMemo(
    () => citations.filter(c => c.origin === 'ai' && !c.verified),
    [citations]
  );
  const verified = useMemo(() => citations.filter(c => c.verified), [citations]);

  if (!candidate) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-white">
        <CheckBadgeIcon className="w-12 h-12 text-gray-300 mb-3" />
        <p className="text-sm text-gray-600">
          Choisissez un candidat pour voir toutes ses citations.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header : récap + action en masse globale */}
      <header className="shrink-0 px-5 py-4 border-b border-gray-200">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-gray-900">Toutes les citations</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {candidate.name} · {citations.length} citation{citations.length > 1 ? 's' : ''} sur{' '}
              {criteria.length} critère{criteria.length > 1 ? 's' : ''} actif{criteria.length > 1 ? 's' : ''}
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <CountBadge
                icon={<SparklesIcon className="w-3 h-3" />}
                label="à valider"
                count={aiPending.length}
                tone="amber"
              />
              <CountBadge
                icon={<ShieldCheckIcon className="w-3 h-3" />}
                label="vérifiées"
                count={verified.length}
                tone="emerald"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => onBulkValidateAi(aiPending.map(c => c.id))}
            disabled={!criteriaValidated || aiPending.length === 0}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            title={
              aiPending.length === 0
                ? 'Aucune citation IA en attente'
                : `Marquer comme vérifiées les ${aiPending.length} citations IA du candidat`
            }
          >
            <ShieldCheckIcon className="w-4 h-4" />
            Tout valider IA
          </button>
        </div>
      </header>

      {/* Bandeau verrouillage si critères non validés */}
      {!criteriaValidated && (
        <div className="shrink-0 px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-start gap-2">
          <LockClosedIcon className="shrink-0 w-4 h-4 text-amber-700 mt-0.5" />
          <div className="text-xs text-amber-900">
            La validation des citations est désactivée tant que les critères ne sont pas validés.
          </div>
        </div>
      )}

      {/* Liste groupée */}
      <div className="flex-1 overflow-y-auto">
        {citations.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-600">
              Aucune citation pour ce candidat pour l'instant.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {groups.map(({ criterion, citations: groupCits }) => {
              const color = colorMap.get(criterion.id);
              const groupPendingIds = groupCits
                .filter(c => c.origin === 'ai' && !c.verified)
                .map(c => c.id);
              const groupVerified = groupCits.filter(c => c.verified).length;
              return (
                <li key={criterion.id} className="px-5 py-4">
                  <div className="flex items-start gap-2 mb-2">
                    {color && (
                      <span
                        className={`shrink-0 w-3 h-3 mt-1 rounded-full ${color.dot}`}
                        aria-hidden
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide tabular-nums">
                          {criterion.code}
                        </span>
                        <span className="text-[11px] font-medium text-gray-400">
                          {criterion.weight} pt{criterion.weight > 1 ? 's' : ''}
                        </span>
                        <span className="text-[11px] font-medium text-gray-500 tabular-nums">
                          · {groupCits.length} citation{groupCits.length > 1 ? 's' : ''}
                          {groupVerified > 0 && ` · ${groupVerified} vérifiée${groupVerified > 1 ? 's' : ''}`}
                        </span>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 leading-snug mt-0.5">
                        {criterion.title}
                      </h3>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      {groupPendingIds.length > 0 && (
                        <button
                          type="button"
                          onClick={() => onBulkValidateAi(groupPendingIds)}
                          disabled={!criteriaValidated}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-violet-700 border border-violet-200 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                          title={`Valider les ${groupPendingIds.length} citations IA de ce critère`}
                        >
                          <ShieldCheckIcon className="w-3 h-3" />
                          Tout valider ({groupPendingIds.length})
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onFocusCriterion(criterion.id)}
                        className="inline-flex items-center gap-0.5 px-1.5 py-1 text-[11px] font-medium text-gray-500 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                        title="Aller sur ce critère"
                      >
                        Voir
                        <ArrowRightIcon className="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  {groupCits.length === 0 ? (
                    <div className="ml-5 p-2 border border-dashed border-gray-200 rounded-lg bg-gray-50 text-center">
                      <p className="text-[11px] text-gray-500">Aucune citation pour ce critère.</p>
                    </div>
                  ) : (
                    <ul className="ml-5 space-y-1.5">
                      {groupCits.map(cit => (
                        <CitationRow
                          key={cit.id}
                          cit={cit}
                          color={color}
                          file={files.find(f => f.id === cit.sourceFileId)}
                          isActive={activeCitationId === cit.id}
                          isHovered={hoveredCitationId === cit.id}
                          criteriaValidated={criteriaValidated}
                          onSelect={() => onSelectCitation(cit.id)}
                          onHoverIn={() => onHoverCitation(cit.id)}
                          onHoverOut={() => onHoverCitation(null)}
                          onToggleVerify={() => onVerifyCitation(cit.id, !cit.verified)}
                          onRemove={() => onRemoveCitation(cit.id)}
                        />
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────

interface CitationRowProps {
  cit: Citation;
  color?: CriterionColor;
  file?: UploadedFile;
  isActive: boolean;
  isHovered: boolean;
  criteriaValidated: boolean;
  onSelect: () => void;
  onHoverIn: () => void;
  onHoverOut: () => void;
  onToggleVerify: () => void;
  onRemove: () => void;
}

const CitationRow: React.FC<CitationRowProps> = ({
  cit,
  color,
  file,
  isActive,
  isHovered,
  criteriaValidated,
  onSelect,
  onHoverIn,
  onHoverOut,
  onToggleVerify,
  onRemove,
}) => {
  const baseBorder = isActive
    ? 'border-violet-400 ring-1 ring-violet-200 bg-violet-50/50'
    : isHovered
    ? 'border-violet-200 bg-violet-50/30'
    : cit.verified
    ? 'border-emerald-200 bg-emerald-50/40'
    : 'border-gray-200 bg-white hover:border-violet-200';
  return (
    <li
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      onClick={onSelect}
      className={`border rounded-lg p-2 cursor-pointer transition-all ${baseBorder}`}
    >
      <div className="flex items-start gap-2">
        {color && (
          <span className={`shrink-0 w-1 self-stretch rounded-full ${color.dot}`} aria-hidden />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-800 leading-relaxed italic line-clamp-2">
            «&nbsp;{cit.text}&nbsp;»
          </p>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-600 min-w-0">
              <span className="truncate max-w-[140px]" title={file?.name}>
                {file?.name ?? 'Fichier supprimé'}
              </span>
              <span className="text-gray-400">·</span>
              <span className="tabular-nums">p. {cit.sourcePage}</span>
            </span>
            {cit.origin === 'manual' ? (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-violet-700 uppercase tracking-wide">
                <PencilSquareIcon className="w-2.5 h-2.5" />
                Manuel
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                <SparklesIcon className="w-2.5 h-2.5" />
                IA
              </span>
            )}
            <div className="ml-auto flex items-center gap-0.5">
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onToggleVerify();
                }}
                disabled={!criteriaValidated}
                className={
                  cit.verified
                    ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
                    : 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
                }
                title={cit.verified ? 'Citation vérifiée (V)' : 'Marquer comme vérifiée (V)'}
              >
                <ShieldCheckIcon className="w-3 h-3" />
                {cit.verified ? 'Vérifiée' : 'Vérifier'}
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onRemove();
                }}
                disabled={!criteriaValidated}
                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Retirer (R)"
              >
                <TrashIcon className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

// ─────────────────────────────────────────────────────────────────

interface CountBadgeProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  tone: 'amber' | 'emerald';
}

const CountBadge: React.FC<CountBadgeProps> = ({ icon, label, count, tone }) => {
  const classes =
    tone === 'amber'
      ? 'text-amber-800 bg-amber-50 border-amber-200'
      : 'text-emerald-800 bg-emerald-50 border-emerald-200';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${classes}`}
    >
      {icon}
      <span className="tabular-nums">{count}</span>
      <span>{label}</span>
    </span>
  );
};

export default AllCitationsPanel;
